import { useMemo } from 'react';
import { getText } from '@storebrand-digital/fms-translations';

import { useAppDispatch, useAppSelector } from '../../../app/store';
import { filterActions } from '../reducer';
import { selectIdToSlugMap } from '../selectors';
import type { FilterChipProps } from '../Filter.Chip';

export const MAX_LABEL_LENGTH = 30;
function truncateLabel(label: string): string {
  if (label.length <= MAX_LABEL_LENGTH) {
    return label;
  }
  return `${label.slice(0, MAX_LABEL_LENGTH - 3)}...`;
}

export function useFilterChipsData(): FilterChipProps[] {
  const dispatch = useAppDispatch();
  const contractNumbers = useAppSelector(
    (state) => state.filter.contractNumbers
  );
  const products = useAppSelector((state) => state.filter.products);
  const categories = useAppSelector((state) => state.filter.categories);
  const idToSlugMap = useAppSelector(selectIdToSlugMap);

  return useMemo(() => {
    const chips: FilterChipProps[] = [];

    (contractNumbers ?? []).forEach((contractNumber) => {
      chips.push({
        key: `contract-number-${contractNumber}`,
        id: `contract-number-${contractNumber}`,
        label: contractNumber,
        value: contractNumber,
        trackId: 'button-remove_contract_number|Avtalenummer',
        onRemove: () =>
          dispatch(filterActions.deselectContractNumber(contractNumber)),
      });
    });

    (products ?? []).forEach((product) => {
      chips.push({
        key: `product-${product}`,
        id: `product-${product}`,
        label: truncateLabel(
          product.startsWith('sharedProductNames.')
            ? getText(product) || product
            : product
        ),
        value: product,
        trackId: 'button-remove_product|Produkt',
        onRemove: () => dispatch(filterActions.deselectProduct(product)),
      });
    });

    (categories ?? []).forEach((category) => {
      chips.push({
        key: `category-${category}`,
        id: `category-${category}`,
        label: truncateLabel(
          getText(`filter.category.${idToSlugMap[category]}`)
        ),
        value: String(category),
        trackId: 'button-remove_category|Kategori',
        onRemove: () => dispatch(filterActions.deselectCategory(category)),
      });
    });

    return chips;
  }, [contractNumbers, products, categories, idToSlugMap, dispatch]);
}
